import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppSettings } from "../../config/app-settings.js";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MenuIcon from "@mui/icons-material/Menu";
import logo from "../../assets/Klargo1.png";
import securityImg from "../../assets/klargo-security-contract.webp";
import Footer from "../Landing/Footer.jsx";
import LoginModal from "../Landing/loginModal.js";

const INK = "#0D2530";
const BODY = "#314A57";
const MUTED = "#6A8593";
const GREEN_SOLID = "#00875A";
const GREEN_BRIGHT = "#00C281";
const MINT = "#E0F8F0";
const PAGE_BG = "#F7FAFC";
const WHITE = "#FFFFFF";
const HAIRLINE = "#E6EEF1";
const CARD_SHADOW = "0 8px 24px rgba(13,37,48,0.08)";
const HEADING_FONT = "'DM Sans', sans-serif";
const BODY_FONT = "'Inter', sans-serif";

const ChecklistItem = ({ index, title, points, variant }) => (
  <Box
    sx={{
      backgroundColor: WHITE,
      borderRadius: "8px",
      p: 1.5,
      border: `1px solid ${HAIRLINE}`,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          height: 20,
          width: 20,
          borderRadius: "4px",
          fontSize: "0.7rem",
          fontWeight: 700,
          flexShrink: 0,
          mt: 0.25,
          backgroundColor: variant === "primary" ? MINT : "#F1F5F9",
          color: variant === "primary" ? GREEN_SOLID : "#475569",
        }}
      >
        {index}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.875rem",
            color: INK,
            lineHeight: 1.4,
            fontFamily: BODY_FONT,
          }}
        >
          {title}
        </Typography>
        <Box component="ul" sx={{ mt: 0.5, pl: 0, listStyle: "none", m: 0 }}>
          {points.map((point, i) => (
            <Box
              key={i}
              component="li"
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                fontSize: "0.875rem",
                color: MUTED,
                lineHeight: 1.4,
                mt: 0.5,
              }}
            >
              <Box
                sx={{
                  mt: "7px",
                  height: 4,
                  width: 4,
                  borderRadius: "50%",
                  flexShrink: 0,
                  backgroundColor: variant === "primary" ? GREEN_BRIGHT : "#94A3B8",
                }}
              />
              {point}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </Box>
);

const ExpandableSummaryCard = ({ summary, details }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <Box>
      {summary}
      <Collapse in={open}>
        <Box sx={{ mt: 2, space: 2 }}>{details}</Box>
      </Collapse>
      <Button
        variant="text"
        size="small"
        onClick={() => setOpen((v) => !v)}
        endIcon={
          <ExpandMoreIcon
            sx={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        }
        sx={{
          mt: 2,
          fontFamily: BODY_FONT,
          fontSize: "0.8rem",
          fontWeight: 600,
          color: GREEN_SOLID,
          textTransform: "none",
          p: 0,
          "&:hover": { background: "none", textDecoration: "underline" },
        }}
      >
        {open ? t("security.debtsRights.showLess") : t("security.debtsRights.readMore")}
      </Button>
    </Box>
  );
};

const SecurityFraud = () => {
  const { t, i18n } = useTranslation();
  const context = useContext(AppSettings);

  const loginRef = useRef(null);
  const loginClose = useRef(null);

  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const langOpen = Boolean(langAnchorEl);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useLayoutEffect(() => {
    context.setAppHeaderNone(true);
    return () => context.setAppHeaderNone(false);
  }, [context]);

  useEffect(() => {
    document.title = t("security.hero.title") + " | Klargo";
  }, [t]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const sections = [
      "hero",
      "akut",
      "akut-detaljer",
      "seriost-vs-bedragare",
      "forebygg",
      "checklista-kopa",
      "vanliga-bedragerier",
      "digital-sakerhet",
      "skulder-rattigheter",
      "hur-klargo-hjalper",
      "faq-bedrageri",
      "ordlista",
    ];
    const handleScroll = () => {
      const triggerLine = 200;
      let current = sections[0];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= triggerLine) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
    setMobileNavOpen(false);
  };

  const handleLangClick = (e) => setLangAnchorEl(e.currentTarget);
  const handleLangClose = () => setLangAnchorEl(null);
  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("appLanguage", lang);
    handleLangClose();
  };

  const detailedSteps = [
    { id: "step-0", title: t("security.emergency.step1.title"), text: t("security.emergency.step1.text") },
    { id: "step-1", title: t("security.emergency.step2.title"), text: t("security.emergency.step2.text") },
    { id: "step-2", title: t("security.emergency.step3.title"), text: t("security.emergency.step3.text") },
    { id: "step-3", title: t("security.emergency.step4.title"), text: t("security.emergency.step4.text") },
    { id: "step-4", title: t("security.emergency.step5.title"), text: t("security.emergency.step5.text") },
    { id: "step-5", title: t("security.emergency.step6.title"), text: t("security.emergency.step6.text") },
  ];

  const navGroups = [
    {
      title: t("security.nav.overview"),
      items: [{ id: "hero", label: t("security.nav.overview") }],
    },
    {
      title: t("security.nav.emergency"),
      items: [
        { id: "akut", label: t("security.nav.suspectFraud") },
        { id: "akut-detaljer", label: t("security.nav.ifYouSuspect") },
      ],
    },
    {
      title: t("security.nav.beforeDeal"),
      items: [
        { id: "seriost-vs-bedragare", label: t("security.nav.legitimateVsFraudsters") },
        { id: "forebygg", label: t("security.nav.fourRules") },
        { id: "checklista-kopa", label: t("security.nav.checklists") },
        { id: "vanliga-bedragerier", label: t("security.nav.commonFrauds") },
        { id: "digital-sakerhet", label: t("security.nav.digitalSecurity") },
      ],
    },
    {
      title: t("security.nav.rightsSupport"),
      items: [
        { id: "skulder-rattigheter", label: t("security.nav.debtsRights") },
        { id: "hur-klargo-hjalper", label: t("security.nav.howKlargoHelps") },
        { id: "faq-bedrageri", label: t("security.nav.faq") },
      ],
    },
    {
      title: t("security.nav.glossary"),
      items: [{ id: "ordlista", label: t("security.nav.glossary") }],
    },
  ];

  const buyerChecklist = [
    { title: t("security.buyer.check1.title"), points: [t("security.buyer.check1.point1"), t("security.buyer.check1.point2")] },
    { title: t("security.buyer.check2.title"), points: [t("security.buyer.check2.point1"), t("security.buyer.check2.point2")] },
    { title: t("security.buyer.check3.title"), points: [t("security.buyer.check3.point1"), t("security.buyer.check3.point2")] },
    { title: t("security.buyer.check4.title"), points: [t("security.buyer.check4.point1"), t("security.buyer.check4.point2")] },
    { title: t("security.buyer.check5.title"), points: [t("security.buyer.check5.point1"), t("security.buyer.check5.point2")] },
    { title: t("security.buyer.check6.title"), points: [t("security.buyer.check6.point1"), t("security.buyer.check6.point2")] },
    { title: t("security.buyer.check7.title"), points: [t("security.buyer.check7.point1")] },
  ];

  const sellerChecklist = [
    { title: t("security.seller.check1.title"), points: [t("security.seller.check1.point1"), t("security.seller.check1.point2")] },
    { title: t("security.seller.check2.title"), points: [t("security.seller.check2.point1"), t("security.seller.check2.point2")] },
    { title: t("security.seller.check3.title"), points: [t("security.seller.check3.point1"), t("security.seller.check3.point2")] },
    { title: t("security.seller.check4.title"), points: [t("security.seller.check4.point1")] },
    { title: t("security.seller.check5.title"), points: [t("security.seller.check5.point1")] },
    { title: t("security.seller.check6.title"), points: [t("security.seller.check6.point1")] },
    { title: t("security.seller.check7.title"), points: [t("security.seller.check7.point1"), t("security.seller.check7.point2")] },
  ];

  const fraudItems = [
    { icon: WarningAmberOutlinedIcon, title: t("security.fraud1.title"), desc: t("security.fraud1.desc"), tips: [t("security.fraud1.tip1"), t("security.fraud1.tip2")] },
    { icon: DescriptionOutlinedIcon, title: t("security.fraud2.title"), desc: t("security.fraud2.desc"), tips: [t("security.fraud2.tip1"), t("security.fraud2.tip2")] },
    { icon: PeopleOutlineIcon, title: t("security.fraud3.title"), desc: t("security.fraud3.desc"), tips: [t("security.fraud3.tip1"), t("security.fraud3.tip2")] },
    { icon: WarningAmberOutlinedIcon, title: t("security.fraud4.title"), desc: t("security.fraud4.desc"), tips: [t("security.fraud4.tip1"), t("security.fraud4.tip2")] },
    { icon: ErrorOutlineIcon, title: t("security.fraud5.title"), desc: t("security.fraud5.desc"), tips: [t("security.fraud5.tip1"), t("security.fraud5.tip2")] },
  ];

  const faqItems = [
    { q: t("security.faq.q1"), a: t("security.faq.a1") },
    { q: t("security.faq.q2"), a: t("security.faq.a2") },
    { q: t("security.faq.q3"), a: t("security.faq.a3") },
    { q: t("security.faq.q4"), a: t("security.faq.a4") },
  ];

  const glossaryItems = [
    { term: t("security.glossary.term1"), def: t("security.glossary.def1") },
    { term: t("security.glossary.term2"), def: t("security.glossary.def2") },
    { term: t("security.glossary.term3"), def: t("security.glossary.def3") },
    { term: t("security.glossary.term4"), def: t("security.glossary.def4") },
    { term: t("security.glossary.term5"), def: t("security.glossary.def5") },
    { term: t("security.glossary.term6"), def: t("security.glossary.def6") },
  ];

  const sectionCardSx = {
    backgroundColor: WHITE,
    border: `1px solid ${HAIRLINE}`,
    borderRadius: "16px",
    p: { xs: 3, md: 4 },
    boxShadow: CARD_SHADOW,
  };

  const DesktopTOC = () => (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        display: { xs: "none", lg: "block" },
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 96,
          maxHeight: "calc(100vh - 120px)",
          overflowY: "auto",
          pr: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: MUTED,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            mb: 2.5,
            pl: 1.5,
            fontFamily: BODY_FONT,
          }}
        >
          {t("security.toc.title")}
        </Typography>
        <Box component="nav" sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {navGroups.map((group, gi) => (
            <Box
              key={group.title}
              sx={
                gi > 0
                  ? { borderTop: `1px solid ${HAIRLINE}`, pt: 2, mt: 2 }
                  : {}
              }
            >
              {!(gi === 0 && group.items.length === 1) && (
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: "#64748B",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 1,
                    pl: 1.5,
                    fontFamily: BODY_FONT,
                  }}
                >
                  {group.title}
                </Typography>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                {group.items.map((item) => {
                  const isActive = activeSection === item.id;
                  return (
                    <Box
                      key={item.id}
                      component="button"
                      onClick={() => scrollToSection(item.id)}
                      sx={{
                        width: "100%",
                        textAlign: "left",
                        px: 1.5,
                        py: 1.25,
                        minHeight: 40,
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        fontFamily: BODY_FONT,
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? "#134E4A" : "#475569",
                        backgroundColor: isActive ? MINT : "transparent",
                        borderLeft: isActive ? `3px solid ${GREEN_SOLID}` : "3px solid transparent",
                        border: "none",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        display: "flex",
                        alignItems: "center",
                        "&:hover": {
                          backgroundColor: isActive ? MINT : "#F1F5F9",
                          color: INK,
                        },
                        "&:focus-visible": {
                          outline: `2px solid ${GREEN_BRIGHT}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{ lineHeight: 1.3, fontSize: "inherit", fontFamily: "inherit", fontWeight: "inherit", color: "inherit" }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: WHITE, fontFamily: BODY_FONT }}>

      {/* ===================== TOP NAV ===================== */}
      <Box
        component="nav"
        role="navigation"
        aria-label="Huvudnavigering"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1200,
          backgroundColor: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.92)",
          backdropFilter: "blur(12px)",
          boxShadow: scrolled ? "0 4px 16px rgba(13,37,48,0.08)" : "none",
          transition: "box-shadow 0.25s ease, background-color 0.25s ease",
          borderBottom: `1px solid ${scrolled ? HAIRLINE : "transparent"}`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: 64,
              py: 1,
            }}
          >
            <Box
              component="a"
              href="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                "&:focus-visible": { outline: `2px solid ${GREEN_BRIGHT}`, outlineOffset: 2, borderRadius: 1 },
              }}
              aria-label="Klargo – gå till startsidan"
            >
              <Box
                component="img"
                src={logo}
                alt="Klargo"
                sx={{ height: 42, width: "auto", objectFit: "contain", display: "block" }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center" }}>
                <Box
                  component="button"
                  onClick={handleLangClick}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.5,
                    border: "none",
                    background: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    fontFamily: BODY_FONT,
                    color: MUTED,
                    transition: "color 0.2s ease, background-color 0.2s ease",
                    "&:hover": { color: INK, backgroundColor: "#F8FAFC" },
                    "&:focus-visible": { outline: `2px solid ${GREEN_BRIGHT}`, outlineOffset: 2 },
                  }}
                >
                  <PublicOutlinedIcon sx={{ fontSize: 16, color: "inherit" }} />
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {i18n.language === "sv" ? "SV" : "EN"}
                  </Box>
                  <ArrowDropDownIcon fontSize="small" />
                </Box>
                <Menu
                  anchorEl={langAnchorEl}
                  open={langOpen}
                  onClose={handleLangClose}
                  PaperProps={{
                    sx: {
                      borderRadius: "12px",
                      border: `1px solid ${HAIRLINE}`,
                      mt: 1,
                      boxShadow: "0 10px 24px rgba(13,37,48,0.10)",
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => changeLanguage("en")}
                    sx={{ fontSize: "13px", gap: 1, "&:hover": { backgroundColor: MINT, color: GREEN_SOLID } }}
                  >
                    <Box component="span" sx={{ fontSize: "0.75rem", fontWeight: 700, color: MUTED }}>EN</Box>
                    English
                  </MenuItem>
                  <MenuItem
                    onClick={() => changeLanguage("sv")}
                    sx={{ fontSize: "13px", gap: 1, "&:hover": { backgroundColor: MINT, color: GREEN_SOLID } }}
                  >
                    <Box component="span" sx={{ fontSize: "0.75rem", fontWeight: 700, color: MUTED }}>SV</Box>
                    Svenska
                  </MenuItem>
                </Menu>
              </Box>

              <Button
                variant="outlined"
                size="small"
                onClick={() => loginRef.current?.click()}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  fontFamily: BODY_FONT,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  borderColor: GREEN_BRIGHT,
                  color: GREEN_SOLID,
                  borderRadius: "8px",
                  px: 2,
                  height: 34,
                  "&:hover": { backgroundColor: MINT, borderColor: GREEN_BRIGHT },
                }}
              >
                {t("landing.loginButton")}
              </Button>

              <IconButton
                aria-label="Öppna meny"
                onClick={(e) => {
                  setMobileMenuAnchor(e.currentTarget);
                  setMobileMenuOpen(true);
                }}
                sx={{ display: { xs: "flex", md: "none" }, color: INK }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ sx: { borderRadius: "12px", mt: 1, minWidth: 200, border: `1px solid ${HAIRLINE}` } }}
              >
                <MenuItem
                  onClick={handleLangClick}
                  sx={{ fontFamily: BODY_FONT, fontSize: "0.9rem", color: BODY, gap: 1 }}
                >
                  <PublicOutlinedIcon sx={{ fontSize: 16 }} />
                  {i18n.language === "sv" ? "Svenska" : "English"}
                  <ArrowDropDownIcon fontSize="small" />
                </MenuItem>
                <MenuItem sx={{ pt: 0.5 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setTimeout(() => loginRef.current?.click(), 0);
                    }}
                    sx={{
                      backgroundColor: GREEN_SOLID,
                      color: WHITE,
                      textTransform: "none",
                      fontFamily: BODY_FONT,
                      fontWeight: 600,
                      borderRadius: "8px",
                      "&:hover": { backgroundColor: "#007A52" },
                    }}
                  >
                    {t("landing.loginButton")}
                  </Button>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ===================== BAND 1: HERO ===================== */}
      <Box sx={{ backgroundColor: PAGE_BG }} id="hero">
        <Container maxWidth="lg" sx={{ py: { xs: 5, lg: 7 } }}>
          <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              component="a"
              href="/"
              sx={{
                fontSize: "0.875rem",
                color: MUTED,
                textDecoration: "none",
                "&:hover": { color: INK },
              }}
            >
              {t("security.breadcrumb.home")}
            </Box>
            <ChevronRightIcon sx={{ fontSize: 16, color: MUTED }} />
            <Typography sx={{ fontSize: "0.875rem", color: INK, fontFamily: BODY_FONT }}>
              {t("security.breadcrumb.title")}
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 4, lg: 5 }} alignItems="flex-start">
            <Grid size={{ xs: 12, lg: 7 }}>
              <Box sx={{ maxWidth: 600 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontWeight: 700,
                    fontSize: { xs: "1.875rem", lg: "2.25rem" },
                    color: INK,
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  {t("security.hero.title")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "1rem", lg: "1.125rem" },
                    color: BODY,
                    lineHeight: 1.7,
                    mb: 3,
                    fontFamily: BODY_FONT,
                  }}
                >
                  {t("security.hero.subtitle")}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: MUTED,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    mb: 1.5,
                    fontFamily: BODY_FONT,
                  }}
                >
                  {t("security.hero.whatYouGet")}
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mb: 3 }}>
                  {[
                    t("security.hero.checklist1"),
                    t("security.hero.checklist2"),
                    t("security.hero.checklist3"),
                  ].map((item, i) => (
                    <Box
                      key={i}
                      component="li"
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        fontSize: "0.875rem",
                        color: INK,
                        lineHeight: 1.6,
                        mb: 1,
                        fontFamily: BODY_FONT,
                      }}
                    >
                      <CheckBoxOutlinedIcon sx={{ fontSize: 16, color: GREEN_SOLID, mt: 0.25, flexShrink: 0 }} />
                      {item}
                    </Box>
                  ))}
                </Box>

                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                  <Button
                    variant="contained"
                    onClick={() => scrollToSection("akut")}
                    startIcon={<WarningAmberOutlinedIcon />}
                    sx={{
                      backgroundColor: GREEN_SOLID,
                      color: WHITE,
                      textTransform: "none",
                      fontFamily: BODY_FONT,
                      fontWeight: 600,
                      fontSize: { xs: "0.875rem", lg: "1rem" },
                      px: { xs: 2.5, lg: 3 },
                      py: { xs: 1, lg: 1.5 },
                      borderRadius: "10px",
                      "&:hover": { backgroundColor: "#007A52" },
                    }}
                  >
                    {t("security.hero.ctaSuspect")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => scrollToSection("forebygg")}
                    startIcon={<SecurityOutlinedIcon />}
                    sx={{
                      borderColor: HAIRLINE,
                      color: INK,
                      backgroundColor: WHITE,
                      textTransform: "none",
                      fontFamily: BODY_FONT,
                      fontWeight: 600,
                      fontSize: { xs: "0.875rem", lg: "1rem" },
                      px: { xs: 2.5, lg: 3 },
                      py: { xs: 1, lg: 1.5 },
                      borderRadius: "10px",
                      "&:hover": { backgroundColor: PAGE_BG },
                    }}
                  >
                    {t("security.hero.ctaPrevent")}
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, lg: 5 }} sx={{ display: { xs: "none", lg: "flex" }, flexDirection: "column", gap: 2 }}>
              <Box
                component="img"
                src={securityImg}
                alt="Klargo köpekontrakt för begagnat fordon med bilnyckel"
                sx={{
                  width: "100%",
                  maxHeight: 200,
                  objectFit: "cover",
                  borderRadius: "12px",
                  border: `1px solid ${HAIRLINE}`,
                  boxShadow: "0 4px 12px rgba(13,37,48,0.06)",
                }}
              />
              <Box
                sx={{
                  backgroundColor: WHITE,
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: "12px",
                  p: 2.5,
                  boxShadow: "0 4px 12px rgba(13,37,48,0.06)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 16, color: MUTED }} />
                  <Typography
                    sx={{ fontSize: "0.875rem", fontWeight: 600, color: INK, fontFamily: HEADING_FONT }}
                  >
                    {t("security.quickOverview.title")}
                  </Typography>
                </Box>
                <Box component="dl" sx={{ m: 0 }}>
                  {[
                    { label: t("security.quickOverview.appliesTo"), value: t("security.quickOverview.appliesToValue") },
                    { label: t("security.quickOverview.forWhom"), value: t("security.quickOverview.forWhomValue") },
                    { label: t("security.quickOverview.lastUpdated"), value: t("security.quickOverview.lastUpdatedValue") },
                  ].map((row, i) => (
                    <Box key={i} sx={{ mb: i < 2 ? 1.5 : 0 }}>
                      <Typography
                        component="dt"
                        sx={{ fontSize: "0.7rem", color: MUTED, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.25, fontFamily: BODY_FONT }}
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        component="dd"
                        sx={{ fontSize: "0.875rem", fontWeight: 600, color: INK, fontFamily: BODY_FONT, m: 0 }}
                      >
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===================== BAND 2: Emergency anchor ===================== */}
      <Box id="akut" />

      {/* ===================== BAND 3: Main content with TOC ===================== */}
      <Box sx={{ backgroundColor: PAGE_BG, py: { xs: 5, lg: 7 }, flex: 1 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", gap: { xs: 0, lg: 5 }, alignItems: "flex-start" }}>

            <DesktopTOC />

            {/* Mobile TOC */}
            <Box sx={{ display: { xs: "block", lg: "none" }, width: "100%", mb: 3 }}>
              <Box
                component="button"
                onClick={() => setMobileNavOpen((v) => !v)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  px: 2,
                  py: 1.5,
                  backgroundColor: WHITE,
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: PAGE_BG },
                  "&:focus-visible": { outline: `2px solid ${GREEN_BRIGHT}`, outlineOffset: 2 },
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: INK, fontFamily: BODY_FONT }}>
                  {t("security.toc.title")}
                </Typography>
                <ExpandMoreIcon
                  sx={{
                    color: MUTED,
                    transform: mobileNavOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </Box>
              <Collapse in={mobileNavOpen}>
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    backgroundColor: WHITE,
                    borderRadius: "8px",
                    border: `1px solid ${HAIRLINE}`,
                  }}
                >
                  {navGroups.map((group, gi) => (
                    <Box key={group.title} sx={gi > 0 ? { mt: 1.5 } : {}}>
                      {!(gi === 0 && group.items.length === 1) && (
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: "#64748B",
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            mb: 0.75,
                            px: 1,
                            fontFamily: BODY_FONT,
                          }}
                        >
                          {group.title}
                        </Typography>
                      )}
                      {group.items.map((item) => (
                        <Box
                          key={item.id}
                          component="button"
                          onClick={() => scrollToSection(item.id)}
                          sx={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            px: 1.5,
                            py: 1,
                            minHeight: 40,
                            borderRadius: "6px",
                            fontSize: "0.875rem",
                            fontFamily: BODY_FONT,
                            fontWeight: activeSection === item.id ? 700 : 500,
                            color: activeSection === item.id ? "#134E4A" : "#475569",
                            backgroundColor: activeSection === item.id ? MINT : "transparent",
                            border: "none",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: PAGE_BG },
                          }}
                        >
                          {item.label}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>

            {/* ===================== MAIN CONTENT ===================== */}
            <Box component="main" sx={{ flex: 1, minWidth: 0, display: { xs: "block", lg: "block" } }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

                {/* Section 1: Emergency steps */}
                <Box id="akut-detaljer" sx={{ scrollMarginTop: "112px", ...sectionCardSx }}>
                  <Box id="misstanke" sx={{ scrollMarginTop: "112px" }} />
                  <Typography
                    variant="h2"
                    sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: "1.25rem", color: INK, mb: 1 }}
                  >
                    {t("security.emergency.title")}
                  </Typography>
                  <Typography sx={{ color: MUTED, mb: 3, lineHeight: 1.7, fontFamily: BODY_FONT, fontSize: "0.9rem" }}>
                    {t("security.emergency.subtitle")}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    {detailedSteps.map((step, index) => (
                      <Box key={step.id} sx={{ display: "flex", gap: 2 }}>
                        <Box
                          sx={{
                            flexShrink: 0,
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            backgroundColor: MINT,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            color: GREEN_SOLID,
                            fontFamily: HEADING_FONT,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box sx={{ flex: 1, pt: 0.5 }}>
                          <Typography sx={{ fontWeight: 600, color: INK, mb: 0.75, fontFamily: BODY_FONT, fontSize: "0.9rem" }}>
                            {step.title}
                          </Typography>
                          <Typography sx={{ fontSize: "0.875rem", color: MUTED, lineHeight: 1.6, fontFamily: BODY_FONT }}>
                            {step.text}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Section 2: Legitimate vs fraudsters */}
                <Box id="seriost-vs-bedragare" sx={{ scrollMarginTop: "112px", ...sectionCardSx }}>
                  <Typography
                    variant="h2"
                    sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: "1.25rem", color: INK, mb: 1 }}
                  >
                    {t("security.legitimateVsFraud.title")}
                  </Typography>
                  <Typography sx={{ color: MUTED, mb: 3, lineHeight: 1.7, fontFamily: BODY_FONT, fontSize: "0.9rem" }}>
                    {t("security.legitimateVsFraud.subtitle")}
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box
                        sx={{
                          backgroundColor: "#F0FDF4",
                          border: "1px solid #BBF7D0",
                          borderRadius: "10px",
                          p: 2.5,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#15803D", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5, fontFamily: BODY_FONT }}
                        >
                          {t("security.legitimateVsFraud.saferContact")}
                        </Typography>
                        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                          {[
                            t("security.legitimateVsFraud.safe1"),
                            t("security.legitimateVsFraud.safe2"),
                            t("security.legitimateVsFraud.safe3"),
                          ].map((item, i) => (
                            <Box
                              key={i}
                              component="li"
                              sx={{ display: "flex", alignItems: "flex-start", gap: 1, fontSize: "0.875rem", color: BODY, fontFamily: BODY_FONT }}
                            >
                              <CheckBoxOutlinedIcon sx={{ fontSize: 16, color: "#16A34A", flexShrink: 0, mt: 0.25 }} />
                              {item}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box
                        sx={{
                          backgroundColor: "#FFFBEB",
                          border: "1px solid #FDE68A",
                          borderRadius: "10px",
                          p: 2.5,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#B45309", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5, fontFamily: BODY_FONT }}
                        >
                          {t("security.legitimateVsFraud.warningSignals")}
                        </Typography>
                        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                          {[
                            t("security.legitimateVsFraud.warning1"),
                            t("security.legitimateVsFraud.warning2"),
                            t("security.legitimateVsFraud.warning3"),
                            t("security.legitimateVsFraud.warning4"),
                          ].map((item, i) => (
                            <Box
                              key={i}
                              component="li"
                              sx={{ display: "flex", alignItems: "flex-start", gap: 1, fontSize: "0.875rem", color: BODY, fontFamily: BODY_FONT }}
                            >
                              <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: "#D97706", flexShrink: 0, mt: 0.25 }} />
                              {item}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ backgroundColor: PAGE_BG, border: `1px solid ${HAIRLINE}`, borderRadius: "10px", p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {[
                      t("security.legitimateVsFraud.info1"),
                      t("security.legitimateVsFraud.info2"),
                      t("security.legitimateVsFraud.info3"),
                    ].map((info, i) => (
                      <Typography key={i} sx={{ fontSize: "0.875rem", color: MUTED, lineHeight: 1.6, fontFamily: BODY_FONT }}>
                        {info}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                {/* Section 3: Four rules */}
                <Box id="forebygg" sx={{ scrollMarginTop: "112px", ...sectionCardSx }}>
                  <Box id="grundregler" sx={{ scrollMarginTop: "112px" }} />
                  <Typography
                    variant="h2"
                    sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: "1.25rem", color: INK, mb: 1 }}
                  >
                    {t("security.fourRules.title")}
                  </Typography>
                  <Typography sx={{ color: MUTED, mb: 3, lineHeight: 1.7, fontFamily: BODY_FONT, fontSize: "0.9rem" }}>
                    {t("security.fourRules.subtitle")}
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { num: 1, title: t("security.fourRules.rule1.title"), points: [t("security.fourRules.rule1.point1"), t("security.fourRules.rule1.point2")] },
                      { num: 2, title: t("security.fourRules.rule2.title"), points: [t("security.fourRules.rule2.point1"), t("security.fourRules.rule2.point2")] },
                      { num: 3, title: t("security.fourRules.rule3.title"), points: [t("security.fourRules.rule3.point1"), t("security.fourRules.rule3.point2")] },
                      { num: 4, title: t("security.fourRules.rule4.title"), points: [t("security.fourRules.rule4.point1"), t("security.fourRules.rule4.point2")] },
                    ].map((rule) => (
                      <Grid key={rule.num} size={{ xs: 12, md: 6 }}>
                        <Box
                          sx={{
                            backgroundColor: PAGE_BG,
                            border: `1px solid ${HAIRLINE}`,
                            borderRadius: "10px",
                            p: 2.5,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                backgroundColor: MINT,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: "0.875rem",
                                color: GREEN_SOLID,
                                fontFamily: HEADING_FONT,
                                flexShrink: 0,
                              }}
                            >
                              {rule.num}
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: INK, mb: 1, fontFamily: BODY_FONT }}>
                                {rule.title}
                              </Typography>
                              <Box component="ul" sx={{ pl: 2, m: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                {rule.points.map((p, i) => (
                                  <Box
                                    key={i}
                                    component="li"
                                    sx={{ fontSize: "0.875rem", color: MUTED, lineHeight: 1.6, fontFamily: BODY_FONT }}
                                  >
                                    {p}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* Section 4: Checklists */}
                <Box
                  id="checklista-kopa"
                  sx={{
                    scrollMarginTop: "112px",
                    backgroundColor: "#FAFBFC",
                    borderRadius: "16px",
                    p: { xs: 3, md: 4 },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: "1.25rem", color: INK, mb: 1 }}
                  >
                    {t("security.checklists.title")}
                  </Typography>
                  <Typography sx={{ color: MUTED, mb: 3, lineHeight: 1.7, fontFamily: BODY_FONT, fontSize: "0.9rem" }}>
                    {t("security.checklists.subtitle")}
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <ShoppingCartOutlinedIcon sx={{ color: GREEN_SOLID, fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 700, color: INK, fontFamily: HEADING_FONT }}>
                          {t("security.checklists.buyerTitle")}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {buyerChecklist.map((item, idx) => (
                          <ChecklistItem key={idx} index={idx + 1} title={item.title} points={item.points} variant="primary" />
                        ))}
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <SellOutlinedIcon sx={{ color: MUTED, fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 700, color: INK, fontFamily: HEADING_FONT }}>
                          {t("security.checklists.sellerTitle")}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {sellerChecklist.map((item, idx) => (
                          <ChecklistItem key={idx} index={idx + 1} title={item.title} points={item.points} variant="neutral" />
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Section 5: Common fraud types (accordion) */}
                <Box id="vanliga-bedragerier" sx={{ scrollMarginTop: "112px", ...sectionCardSx }}>
                  <Typography
                    variant="h2"
                    sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: "1.25rem", color: INK, mb: 1 }}
                  >
                    {t("security.commonFrauds.title")}
                  </Typography>
                  <Typography sx={{ color: MUTED, mb: 3, lineHeight: 1.7, fontFamily: BODY_FONT, fontSize: "0.9rem" }}>
                    {t("security.commonFrauds.subtitle")}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {fraudItems.map((fraud, i) => {
                      const FraudIcon = fraud.icon;
                      return (
                        <Accordion
                          key={i}
                          disableGutters
                          elevation={0}
                          sx={{
                            backgroundColor: WHITE,
                            border: `1px solid ${HAIRLINE}`,
                            borderRadius: "8px !important",
                            "&:before": { display: "none" },
                            "&.Mui-expanded": { border: `1px solid ${HAIRLINE}`, backgroundColor: PAGE_BG },
                            overflow: "hidden",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: MUTED, fontSize: 18 }} />}
                            sx={{ px: 2, py: 1, "& .MuiAccordionSummary-content": { my: 0.5 } }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 24,
                                  height: 24,
                                  borderRadius: "4px",
                                  backgroundColor: "#FEF3C7",
                                  flexShrink: 0,
                                }}
                              >
                                <FraudIcon sx={{ fontSize: 14, color: "#B45309" }} />
                              </Box>
                              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: INK, fontFamily: BODY_FONT }}>
                                {fraud.title}
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2, pb: 2.5, pt: 0.5 }}>
                            <Box sx={{ pl: 4.5 }}>
                              <Typography sx={{ fontSize: "0.875rem", color: MUTED, mb: 2, lineHeight: 1.6, fontFamily: BODY_FONT }}>
                                {fraud.desc}
                              </Typography>
                              <Box
                                sx={{
                                  backgroundColor: PAGE_BG,
                                  border: `1px solid ${HAIRLINE}`,
                                  borderRadius: "8px",
                                  p: 2,
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                                  <SecurityOutlinedIcon sx={{ fontSize: 14, color: GREEN_SOLID }} />
                                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: INK, fontFamily: BODY_FONT }}>
                                    {t("security.commonFrauds.howToProtect")}
                                  </Typography>
                                </Box>
                                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                                  {fraud.tips.map((tip, j) => (
                                    <Box
                                      key={j}
                                      component="li"
                                      sx={{ display: "flex", alignItems: "flex-start", gap: 1, fontSize: "0.875rem", color: MUTED, fontFamily: BODY_FONT }}
                                    >
                                      <CheckBoxOutlinedIcon sx={{ fontSize: 14, color: GREEN_SOLID, flexShrink: 0, mt: 0.25 }} />
                                      {tip}
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                </Box>

                {/* Section 6: Digital security */}
                <Box id="digital-sakerhet" sx={{ scrollMarginTop: "112px", ...sectionCardSx }}>
                  <Typography
                    variant="h2"
                    sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: "1.25rem", color: INK, mb: 1 }}
                  >
                    {t("security.digitalSecurity.title")}
                  </Typography>
                  <Typography sx={{ color: MUTED, mb: 3, lineHeight: 1.7, fontFamily: BODY_FONT, fontSize: "0.9rem" }}>
                    {t("security.digitalSecurity.subtitle")}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: PAGE_BG,
                      border: `1px solid ${HAIRLINE}`,
                      borderRadius: "10px",
                      p: 2.5,
                      mb: 3,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "0.7rem", fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5, fontFamily: BODY_FONT }}
                    >
                      {t("security.digitalSecurity.basicAdvice")}
                    </Typography>
                    <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                      {[
                        t("security.digitalSecurity.advice1"),
                        t("security.digitalSecurity.advice2"),
                        t("security.digitalSecurity.advice3"),
                        t("security.digitalSecurity.advice4"),
                      ].map((item, i) => (
                        <Box
                          key={i}
                          component="li"
                          sx={{ display: "flex", alignItems: "flex-start", gap: 1, fontSize: "0.875rem", color: BODY, fontFamily: BODY_FONT }}
                        >
                          <CheckBoxOutlinedIcon sx={{ fontSize: 16, color: GREEN_SOLID, flexShrink: 0, mt: 0.25 }} />
                          {item}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {[t("security.digitalSecurity.info1"), t("security.digitalSecurity.info2")].map((info, i) => (
                      <Typography key={i} sx={{ fontSize: "0.875rem", color: MUTED, lineHeight: 1.6, fontFamily: BODY_FONT }}>
                        {info}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                {/* Section 7: Debts and rights (expandable) */}
                <Box id="skulder-rattigheter" sx={{ scrollMarginTop: "112px", ...sectionCardSx }}>
                  <Typography
                    variant="h2"
                    sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: "1.25rem", color: INK, mb: 2 }}
                  >
                    {t("security.debtsRights.title")}
                  </Typography>
                  <ExpandableSummaryCard
                    summary={
                      <Box>
                        <Box
                          sx={{
                            backgroundColor: PAGE_BG,
                            border: `1px solid ${HAIRLINE}`,
                            borderRadius: "10px",
                            p: 2,
                            mb: 2,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: "0.7rem", fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5, fontFamily: BODY_FONT }}
                          >
                            {t("security.debtsRights.shortVersion")}
                          </Typography>
                          <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                            {[
                              t("security.debtsRights.point1"),
                              t("security.debtsRights.point2"),
                              t("security.debtsRights.point3"),
                            ].map((item, i) => (
                              <Box
                                key={i}
                                component="li"
                                sx={{ display: "flex", alignItems: "flex-start", gap: 1, fontSize: "0.875rem", color: BODY, fontFamily: BODY_FONT }}
                              >
                                <CheckBoxOutlinedIcon sx={{ fontSize: 14, color: GREEN_SOLID, flexShrink: 0, mt: 0.25 }} />
                                {item}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            borderLeft: "4px solid #3B82F6",
                            backgroundColor: "#EFF6FF",
                            borderRadius: "0 8px 8px 0",
                            p: 2,
                          }}
                        >
                          <Typography sx={{ fontWeight: 600, color: INK, mb: 1.5, fontSize: "0.875rem", fontFamily: BODY_FONT }}>
                            {t("security.debtsRights.alwaysSave")}
                          </Typography>
                          <Grid container spacing={1}>
                            {[
                              t("security.debtsRights.save1"),
                              t("security.debtsRights.save2"),
                              t("security.debtsRights.save3"),
                              t("security.debtsRights.save4"),
                            ].map((item, i) => (
                              <Grid key={i} size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.875rem", color: MUTED, fontFamily: BODY_FONT }}>
                                  <DescriptionOutlinedIcon sx={{ fontSize: 14, color: "#3B82F6", flexShrink: 0 }} />
                                  {item}
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Box>
                    }
                    details={
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Typography sx={{ fontSize: "0.875rem", color: MUTED, lineHeight: 1.6, fontFamily: BODY_FONT }}>
                          {t("security.debtsRights.details1")}
                        </Typography>
                        <Typography sx={{ fontSize: "0.875rem", color: MUTED, lineHeight: 1.6, fontFamily: BODY_FONT }}>
                          {t("security.debtsRights.details2")}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                {/* Section 8: How Klargo helps */}
                <Box id="hur-klargo-hjalper" sx={{ scrollMarginTop: "112px", ...sectionCardSx }}>
                  <Typography
                    variant="h2"
                    sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: "1.25rem", color: INK, mb: 1 }}
                  >
                    {t("security.klargoHelps.title")}
                  </Typography>
                  <Typography sx={{ color: MUTED, mb: 3, lineHeight: 1.7, fontFamily: BODY_FONT, fontSize: "0.9rem" }}>
                    {t("security.klargoHelps.subtitle")}
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Box sx={{ backgroundColor: WHITE, border: `1px solid ${HAIRLINE}`, borderRadius: "10px", p: 2.5, height: "100%" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                          <CheckBoxOutlinedIcon sx={{ fontSize: 16, color: GREEN_SOLID }} />
                          <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: INK, fontFamily: BODY_FONT }}>
                            {t("security.klargoHelps.withKlargo")}
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                          {[
                            t("security.klargoHelps.feature1"),
                            t("security.klargoHelps.feature2"),
                            t("security.klargoHelps.feature3"),
                          ].map((item, i) => (
                            <Box
                              key={i}
                              component="li"
                              sx={{ display: "flex", alignItems: "flex-start", gap: 1, fontSize: "0.875rem", color: MUTED, fontFamily: BODY_FONT }}
                            >
                              <CheckBoxOutlinedIcon sx={{ fontSize: 16, color: GREEN_SOLID, flexShrink: 0, mt: 0.25 }} />
                              {item}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Box sx={{ backgroundColor: PAGE_BG, border: `1px solid ${HAIRLINE}`, borderRadius: "10px", p: 2.5, height: "100%" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                          <ErrorOutlineIcon sx={{ fontSize: 16, color: MUTED }} />
                          <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: INK, fontFamily: BODY_FONT }}>
                            {t("security.klargoHelps.important")}
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                          {[
                            t("security.klargoHelps.note1"),
                            t("security.klargoHelps.note2"),
                            t("security.klargoHelps.note3"),
                          ].map((item, i) => (
                            <Box
                              key={i}
                              component="li"
                              sx={{ display: "flex", alignItems: "flex-start", gap: 1, fontSize: "0.875rem", color: MUTED, fontFamily: BODY_FONT }}
                            >
                              <Box sx={{ color: "#94A3B8", mt: 0.5, flexShrink: 0, lineHeight: 1 }}>•</Box>
                              {item}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {[t("security.klargoHelps.info1"), t("security.klargoHelps.info2")].map((info, i) => (
                      <Typography key={i} sx={{ fontSize: "0.875rem", color: MUTED, lineHeight: 1.6, fontFamily: BODY_FONT }}>
                        {info}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                {/* Section 9: FAQ (accordion) */}
                <Box id="faq-bedrageri" sx={{ scrollMarginTop: "112px", ...sectionCardSx }}>
                  <Typography
                    variant="h2"
                    sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: "1.25rem", color: INK, mb: 2 }}
                  >
                    {t("security.faq.title")}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {faqItems.map((item, i) => (
                      <Accordion
                        key={i}
                        disableGutters
                        elevation={0}
                        sx={{
                          backgroundColor: WHITE,
                          border: `1px solid ${HAIRLINE}`,
                          borderRadius: "8px !important",
                          "&:before": { display: "none" },
                          "&.Mui-expanded": { border: `1px solid ${HAIRLINE}`, backgroundColor: PAGE_BG },
                          overflow: "hidden",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: MUTED, fontSize: 18 }} />}
                          sx={{ px: 2, py: 1, "& .MuiAccordionSummary-content": { my: 0.5 } }}
                        >
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 24,
                                height: 24,
                                borderRadius: "4px",
                                backgroundColor: MINT,
                                flexShrink: 0,
                                mt: 0.25,
                              }}
                            >
                              <HelpOutlineIcon sx={{ fontSize: 14, color: GREEN_SOLID }} />
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: INK, fontFamily: BODY_FONT }}>
                              {item.q}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2, pb: 2.5, pt: 0.5 }}>
                          <Typography sx={{ fontSize: "0.875rem", color: MUTED, lineHeight: 1.6, fontFamily: BODY_FONT, pl: 4.5 }}>
                            {item.a}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                </Box>

                {/* Section 10: Glossary */}
                <Box id="ordlista" sx={{ scrollMarginTop: "112px", ...sectionCardSx }}>
                  <Typography
                    variant="h2"
                    sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: "1.25rem", color: INK, mb: 2 }}
                  >
                    {t("security.glossary.title")}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {glossaryItems.map((item, i) => (
                      <Box
                        key={i}
                        sx={{
                          backgroundColor: WHITE,
                          borderLeft: `2px solid ${HAIRLINE}`,
                          border: `1px solid ${HAIRLINE}`,
                          borderLeftWidth: 2,
                          borderLeftColor: "#94A3B8",
                          borderRadius: "0 8px 8px 0",
                          px: 2,
                          py: 1.5,
                        }}
                      >
                        <Typography
                          component="dt"
                          sx={{ fontWeight: 600, fontSize: "0.875rem", color: INK, fontFamily: BODY_FONT }}
                        >
                          {item.term}
                        </Typography>
                        <Typography
                          component="dd"
                          sx={{ fontSize: "0.875rem", color: MUTED, fontFamily: BODY_FONT, m: 0 }}
                        >
                          {item.def}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

              </Box>

              {/* Article footer: Sources */}
              <Box
                component="footer"
                sx={{ mt: 4, ...sectionCardSx }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5 }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: PAGE_BG,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <DescriptionOutlinedIcon sx={{ fontSize: 20, color: MUTED }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{ fontSize: "0.875rem", fontWeight: 600, color: BODY, mb: 2, fontFamily: BODY_FONT }}
                    >
                      {t("security.sources.title")}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {[
                        t("security.sources.info1"),
                        t("security.sources.info2"),
                        t("security.sources.info3"),
                      ].map((info, i) => (
                        <Typography key={i} sx={{ fontSize: "0.875rem", color: "#475569", lineHeight: 1.6, fontFamily: BODY_FONT }}>
                          {info}
                        </Typography>
                      ))}
                    </Box>
                    <Box sx={{ mt: 2.5 }}>
                      <Box
                        component="a"
                        href="/kontakt"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: GREEN_SOLID,
                          textDecoration: "none",
                          fontFamily: BODY_FONT,
                          "&:hover": { textDecoration: "underline" },
                          "&:focus-visible": { outline: `2px solid ${GREEN_BRIGHT}`, outlineOffset: 2, borderRadius: 1 },
                        }}
                      >
                        {t("security.sources.contactUs")}
                        <ChevronRightIcon sx={{ fontSize: 12 }} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer
        logo={logo}
        onScrollToQuestion={() => {
          const el = document.getElementById("faq-bedrageri");
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }}
      />

      <LoginModal open={loginRef} close={loginClose} />
    </Box>
  );
};

export default SecurityFraud;
